import { StoreWrapperInterface, STORE_WRAPPER_TOKEN, UserInterface, ApplicationsEnum } from '@actassa/api';
import { Inject, Injectable } from '@angular/core';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Actions, ofActionDispatched, Select } from '@ngxs/store';
import { formatISO, formatISO9075, parseJSON } from 'date-fns';
import { toDate, utcToZonedTime } from 'date-fns-tz';
import { differenceBy, isEqual, last, sortBy } from 'lodash-es';
import moment from 'moment';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { BehaviorSubject, combineLatest, concat, merge, NEVER, Observable, of, Subscription } from 'rxjs';
import { catchError, delay, distinctUntilChanged, map, mapTo, reduce, scan, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { merge as lodashMerge } from 'lodash-es';

import { DeleteDataCollection } from '../+state/actions/delete-data-collection';
import { LoadDataCollectionsSuccess } from '../+state/actions/load-data-collections-success';
import { ResetState } from '../+state/actions/reset-state';
import { SaveDataCollection } from '../+state/actions/save-data-collection';
import { UpdateDataCollection } from '../+state/actions/update-data-collection';
import { DataCollectionState } from '../+state/data-collection.state';
import { IDB_DATA_COLLECTIONS_STORE, IDB_INDEX_TEMPLATE_ID, IDB_INDEX_UUID } from '../constants/idb.config';
import { valueExtractorHelper } from '../helpers/value-extractor.helper';
import { DataCollectionInterface } from '../interfaces/data-collection.interface';
import { InspectionListItemInterface } from '../interfaces/inspection-list-item.interface';
import { DataCollectionType } from '../types/inspection-type';

@Injectable({
    providedIn: 'root',
})
export class DataCollectionsStoreService {
    @Select(DataCollectionState.inspections$) private inspections$: Observable<Array<DataCollectionInterface>>;

    public readonly dataCollectionsActivator$ = new BehaviorSubject(null);

    private subscriptions: Subscription;

    constructor(
        @Inject(STORE_WRAPPER_TOKEN) private readonly mainStore: StoreWrapperInterface,
        private readonly actions$: Actions,
        private readonly dbService: NgxIndexedDBService,
    ) {
        this.isActionSubscriptionEnabled$
            .pipe(
                distinctUntilChanged(),
                tap((isAuthenticated: boolean) => {
                    if (isAuthenticated) {
                        const migratesSubscription = this.migrate$().subscribe();
                        const initSubscription = this.init$().subscribe();

                        this.subscriptions = new Subscription();
                        this.subscriptions.add(migratesSubscription);
                        this.subscriptions.add(initSubscription);

                        return;
                    }

                    this.subscriptions?.unsubscribe();
                }),
            )
            .subscribe();
    }

    private init$(): Observable<unknown> {
        return merge(
            this.actions$.pipe(
                ofActionDispatched(DeleteDataCollection),
                distinctUntilChanged(isEqual),
                switchMap(({ dataCollection }: DeleteDataCollection) =>
                    this.dbService.update(IDB_DATA_COLLECTIONS_STORE, dataCollection)),
            ),
            this.actions$.pipe(
                ofActionDispatched(SaveDataCollection),
                distinctUntilChanged(isEqual),
                switchMap(({ dataCollection }: SaveDataCollection) =>
                    this.dbService.update(IDB_DATA_COLLECTIONS_STORE, dataCollection)),
            ),
            this.actions$.pipe(
                ofActionDispatched(UpdateDataCollection),
                distinctUntilChanged(isEqual),
                switchMap(({ dataCollection }: UpdateDataCollection) => {
                    return this.dbService.getByKey<DataCollectionInterface>(IDB_DATA_COLLECTIONS_STORE, dataCollection.id)
                        .pipe(
                            switchMap((savedDataCollection: DataCollectionInterface) => {
                                const updatedDataCollection: DataCollectionInterface = lodashMerge({}, savedDataCollection, dataCollection);

                                return this.dbService.update(IDB_DATA_COLLECTIONS_STORE, updatedDataCollection);
                            }),
                        );
                })),
            this.actions$.pipe(
                ofActionDispatched(LoadDataCollectionsSuccess),
                distinctUntilChanged(isEqual),
                switchMap(({ dataCollections }: LoadDataCollectionsSuccess) => this.saveLoadedDataCollections$(dataCollections)),
            ),
        ).pipe(
            delay(0),
            tap(() => this.activate()),
            catchError((error) => {
                this.mainStore.showToast(error.message);

                return NEVER;
            }),
        );
    }

    public getDataCollections$(types: Array<DataCollectionType>): Observable<Array<InspectionListItemInterface>> {
        return this.dataCollectionsActivator$
            .pipe(
                withLatestFrom(this.mainStore.user$),
                switchMap(([, { uuid }]: [null, UserInterface]) =>
                    this.dbService.getAllByIndex<DataCollectionInterface>(
                        IDB_DATA_COLLECTIONS_STORE, IDB_INDEX_UUID, IDBKeyRange.only(uuid),
                    )),
                map((dataCollections: Array<DataCollectionInterface>) =>
                    dataCollections.filter(dataCollection => !dataCollection.isDeleted)),
                map((dataCollections: Array<DataCollectionInterface>) =>
                    this.filterTemplatesByType(dataCollections, types)),
                map((dataCollections: Array<DataCollectionInterface>) =>
                    dataCollections.map(this.mapInspectionToListItem)),
                map((dataCollections: Array<InspectionListItemInterface>) =>
                    dataCollections.sort(this.sortListItems)),
            );
    }

    public getDataCollection$(id: string): Observable<DataCollectionInterface> {
        return this.dbService.getByKey<DataCollectionInterface>(IDB_DATA_COLLECTIONS_STORE, id);
    }

    public getActiveDataCollectionByTemplateId$(templateId: string): Observable<DataCollectionInterface | undefined> {
        return this.dbService.getAllByIndex<DataCollectionInterface>(
            IDB_DATA_COLLECTIONS_STORE, IDB_INDEX_TEMPLATE_ID, IDBKeyRange.only(templateId),
        )
            .pipe(
                map(dataCollections => {
                    const activeDataCollections = dataCollections
                        .filter(dataCollection => !dataCollection.isDeleted && !dataCollection.isFinished);

                    return last(sortBy(activeDataCollections, ['modifiedAt']));
                }),
            );
    }

    public activate(): void {
        this.dataCollectionsActivator$.next(null);
    }

    public saveLoadedDataCollections$(
        dataCollections: Array<DataCollectionInterface>,
    ): Observable<unknown> {
        // TODO: Здесь есть проблема!
        // при перелогине на запущеном приложении значение user$ обновляется
        // в результате просиходит отправка dataCollections,
        // так как для нового пользователя они "новые".
        return this.mainStore.user$
            .pipe(
                switchMap(({ uuid }: UserInterface) => this.getLimitedDataCollections$(uuid)),
                withLatestFrom(this.mainStore.timezone$),
                switchMap(([localDataCollections, timezone]: [Array<DataCollectionInterface>, string]) => {
                    const differenceByKeyName = 'id';
                    const newLocalDataCollections = differenceBy(localDataCollections, dataCollections, differenceByKeyName);
                    const existLocalDataCollections = differenceBy(localDataCollections, newLocalDataCollections, differenceByKeyName);
                    const newRemoteDataCollections = differenceBy(dataCollections, localDataCollections, differenceByKeyName);
                    const existRemoteDataCollections = differenceBy(dataCollections, newRemoteDataCollections, differenceByKeyName);
                    const dataCollectionsForUpdate: Array<DataCollectionInterface> = [];
                    const existDataCollections = existLocalDataCollections
                        .reduce((accum: Array<DataCollectionInterface>, existLocalDataCollection: DataCollectionInterface) => {
                            const existRemoteDataCollection = existRemoteDataCollections
                                .find((existRemote: DataCollectionInterface) =>
                                    existRemote.id === existLocalDataCollection.id);

                            if (!existRemoteDataCollection) {
                                return accum;
                            }

                            if (existRemoteDataCollection.isFinished || existRemoteDataCollection.isDeleted) {
                                return [...accum, existRemoteDataCollection];
                            }

                            if (existRemoteDataCollection.modifiedAt >= existLocalDataCollection.modifiedAt) {
                                return [...accum, existRemoteDataCollection];
                            }

                            // NOTE: удаленные лакальные dataCollection тоже предаем на сервер
                            // на случай "случайного" удаления для восстановления
                            // это "немного" избыточно, так как просто ненужных удаленных
                            // будет много больше

                            dataCollectionsForUpdate.push(existLocalDataCollection);

                            return [...accum, existLocalDataCollection];
                        }, []);

                    [...newLocalDataCollections, ...dataCollectionsForUpdate]
                        .forEach((inspection: DataCollectionInterface) => this.updateDataCollection(inspection));

                    const updatedLocalDataCollections = [
                        ...existDataCollections,
                        ...newLocalDataCollections,
                        ...newRemoteDataCollections,
                    ]
                        .filter(({ mustFinishBy, isFinished, isDeleted }) => {
                            if (isDeleted) {
                                return false;
                            }

                            if (isFinished) {
                                return true;
                            }

                            if (mustFinishBy) {
                                return this.getCurrentTimezoneDateTime(timezone) < mustFinishBy;
                            }

                            return true;
                        });

                    const localDataCollectionsIds = localDataCollections.map(({ id }) => id);

                    return this.dbService.bulkDelete(IDB_DATA_COLLECTIONS_STORE, localDataCollectionsIds)
                        .pipe(
                            switchMap(() => concat(...updatedLocalDataCollections.map(dataCollection => this.dbService.add(IDB_DATA_COLLECTIONS_STORE, dataCollection)))),
                        );
                }),
            );
    }

    private getLimitedDataCollections$(uuid: string): Observable<Array<DataCollectionInterface>> {
        return this.dbService.getAllByIndex<DataCollectionInterface>(
            IDB_DATA_COLLECTIONS_STORE, IDB_INDEX_UUID, IDBKeyRange.only(uuid),
        ).pipe(
            withLatestFrom(this.mainStore.getMenuItemProperty$('COMMON_CONFIG', 'dataCollectionMaximumStorageDays')),
            map(([dataCollections, days = 30]: [Array<DataCollectionInterface>, number]) => {
                const currentDate = new Date();

                currentDate.setDate(currentDate.getDate() - days);

                const timestamp = currentDate.valueOf();

                return dataCollections.filter(({ modifiedAt }) => modifiedAt > timestamp);
            }),
        );
    }

    private filterTemplatesByType(
        dataCollections: Array<DataCollectionInterface>,
        types: Array<DataCollectionType>,
    ): Array<DataCollectionInterface> {
        return dataCollections.filter(({ type }: DataCollectionInterface) => types.includes(type));
    }

    private mapInspectionToListItem(dataCollection: DataCollectionInterface): InspectionListItemInterface {
        const titleAddKey = dataCollection.titleAddKey || dataCollection.clientNameKey;
        const { value: titleAdd } = dataCollection.questions
            .find(question => question.key === titleAddKey) || {};
        const { value: inspectionDate } = dataCollection.questions
            .find(question => question.key === dataCollection.inspectionDateKey) || {};

        let validatedDataCollectionDate;

        try {
            validatedDataCollectionDate = formatISO(parseJSON(inspectionDate));
        } catch (error) {
            validatedDataCollectionDate = null;
        }

        return {
            id: dataCollection.id,
            inspectionDate: validatedDataCollectionDate,
            isFinished: dataCollection.isFinished,
            isOpened: dataCollection.isOpened,
            title: dataCollection.title,
            titleAdd: valueExtractorHelper(titleAdd),
            type: dataCollection.type,
        };
    }

    private sortListItems(a: InspectionListItemInterface, b: InspectionListItemInterface): number {
        if (!b.inspectionDate) {
            return -1;
        }

        if (!a.inspectionDate) {
            return 1;
        }

        const prevMoment: moment.Moment = moment(a.inspectionDate);
        const nextMoment: moment.Moment = moment(b.inspectionDate);

        if (prevMoment.isAfter(nextMoment)) {
            return -1;
        }

        if (prevMoment.isBefore(nextMoment)) {
            return 1;
        }

        return 0;
    }

    private getCurrentTimezoneDateTime(timezone: string): string {
        return formatISO9075(utcToZonedTime(toDate(new Date(), { timeZone: timezone }), timezone));
    }

    private migrate$(): Observable<unknown> {
        return this.inspections$
            .pipe(
                take(1),
                switchMap((inspections: Array<DataCollectionInterface>) => {
                    if (inspections?.length) {
                        this.resetState();

                        return concat(...inspections.map(inspection => this.dbService.add(IDB_DATA_COLLECTIONS_STORE, inspection)));
                    }

                    return of(null);
                }),
            );
    }

    private get isActionSubscriptionEnabled$(): Observable<boolean> {
        const { isAuthenticated$, app$ } = this.mainStore;
        const enabledRoles$ = app$
            .pipe(
                map((app: ApplicationsEnum) => Boolean(app)),
            );

        return combineLatest([isAuthenticated$, enabledRoles$])
            .pipe(
                map((keys: Array<boolean>) => keys.every(Boolean)),
            );
    }

    @Dispatch()
    private updateDataCollection(dataCollection: DataCollectionInterface): UpdateDataCollection {
        return new UpdateDataCollection(dataCollection);
    }

    @Dispatch()
    private resetState(): ResetState {
        return new ResetState();
    }
}
